// React Query hooks for student panel

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as studentService from '../services/studentService';
import type {
  GetStudentClassesQueryDto,
  SubmitAssignmentDto,
  UpdateSubmissionDto,
} from '@/types/student.types';

// ==================== Query Keys ====================

// Per-course reads are year-scoped: a past year (read-only) and the current
// year must never share a cache entry. Absent year = current.
const yearKey = (academicYearId?: string): string => academicYearId ?? 'current';
// Course instance: the former group's id when consulting a course of a group
// the student has left; absent = the current group.
const groupKey = (groupeId?: string): string => groupeId ?? 'current-group';

export const studentKeys = {
  all: ['student'] as const,

  // Academic years the student can consult
  academicYears: () => [...studentKeys.all, 'academic-years'] as const,

  // My Classes
  classes: () => [...studentKeys.all, 'classes'] as const,
  classesList: (query: GetStudentClassesQueryDto) => [...studentKeys.classes(), 'list', query] as const,
  classDetail: (moduleElementId: string, academicYearId?: string, groupeId?: string) =>
    [...studentKeys.classes(), 'detail', moduleElementId, yearKey(academicYearId), groupKey(groupeId)] as const,

  // Content
  content: (moduleElementId: string, academicYearId?: string) =>
    [...studentKeys.all, 'content', moduleElementId, yearKey(academicYearId)] as const,
  announcements: (moduleElementId: string, academicYearId?: string, groupeId?: string) =>
    [...studentKeys.all, 'announcements', moduleElementId, yearKey(academicYearId), groupKey(groupeId)] as const,
  syllabus: (moduleElementId: string, academicYearId?: string) =>
    [...studentKeys.all, 'syllabus', moduleElementId, yearKey(academicYearId)] as const,

  // Grades
  grades: (moduleElementId: string, academicYearId?: string, groupeId?: string) =>
    [...studentKeys.all, 'grades', moduleElementId, yearKey(academicYearId), groupKey(groupeId)] as const,

  // Attendance
  attendance: (moduleElementId: string, academicYearId?: string, groupeId?: string) =>
    [...studentKeys.all, 'attendance', moduleElementId, yearKey(academicYearId), groupKey(groupeId)] as const,

  // Classmates
  classmates: (groupeId: string) =>
    [...studentKeys.all, 'classmates', groupeId] as const,

  // Assignments
  assignments: (moduleElementId: string, academicYearId?: string, groupeId?: string) =>
    [...studentKeys.all, 'assignments', moduleElementId, yearKey(academicYearId), groupKey(groupeId)] as const,
  assignmentDetail: (assignmentId: string, academicYearId?: string) =>
    [...studentKeys.all, 'assignment', assignmentId, yearKey(academicYearId)] as const,
  submission: (assignmentId: string) =>
    [...studentKeys.all, 'submission', assignmentId] as const,
};

// ==================== Academic Years ====================

export function useStudentAcademicYears() {
  return useQuery({
    queryKey: studentKeys.academicYears(),
    queryFn: () => studentService.fetchStudentAcademicYears(),
    staleTime: 10 * 60 * 1000,
  });
}

// ==================== My Classes Hooks ====================

export function useMyClasses(query: GetStudentClassesQueryDto = {}) {
  return useQuery({
    queryKey: studentKeys.classesList(query),
    queryFn: () => studentService.fetchMyClasses(query),
    staleTime: 30000,
  });
}

export function useClassDetail(moduleElementId: string | null, academicYearId?: string, groupeId?: string) {
  return useQuery({
    queryKey: studentKeys.classDetail(moduleElementId || '', academicYearId, groupeId),
    queryFn: () => studentService.fetchClassDetail(moduleElementId!, academicYearId, groupeId),
    enabled: !!moduleElementId,
    staleTime: 30000,
  });
}

// ==================== Content Hooks ====================

export function useCourseContent(moduleElementId: string | null, academicYearId?: string, groupeId?: string) {
  return useQuery({
    queryKey: [...studentKeys.content(moduleElementId || '', academicYearId), groupeId],
    queryFn: () => studentService.fetchCourseContent(moduleElementId!, academicYearId, groupeId),
    enabled: !!moduleElementId,
    staleTime: 60000,
  });
}

export function useAnnouncements(moduleElementId: string | null, academicYearId?: string, groupeId?: string) {
  return useQuery({
    queryKey: studentKeys.announcements(moduleElementId || '', academicYearId, groupeId),
    queryFn: () => studentService.fetchAnnouncements(moduleElementId!, academicYearId, groupeId),
    enabled: !!moduleElementId,
    staleTime: 30000,
  });
}

export function useSyllabus(moduleElementId: string | null, academicYearId?: string, groupeId?: string) {
  return useQuery({
    queryKey: [...studentKeys.syllabus(moduleElementId || '', academicYearId), groupeId],
    queryFn: () => studentService.fetchSyllabus(moduleElementId!, academicYearId, groupeId),
    enabled: !!moduleElementId,
    staleTime: 60000,
  });
}

// ==================== Grades Hooks ====================

export function useMyGrades(moduleElementId: string | null, academicYearId?: string, groupeId?: string) {
  return useQuery({
    queryKey: studentKeys.grades(moduleElementId || '', academicYearId, groupeId),
    queryFn: () => studentService.fetchMyGrades(moduleElementId!, academicYearId, groupeId),
    enabled: !!moduleElementId,
    staleTime: 60000,
  });
}

// ==================== Attendance Hooks ====================

export function useMyAttendance(moduleElementId: string | null, academicYearId?: string, groupeId?: string) {
  return useQuery({
    queryKey: studentKeys.attendance(moduleElementId || '', academicYearId, groupeId),
    queryFn: () => studentService.fetchMyAttendance(moduleElementId!, academicYearId, groupeId),
    enabled: !!moduleElementId,
    staleTime: 60000,
  });
}

// ==================== Classmates Hooks ====================

export function useClassmates(groupeId: string | null) {
  return useQuery({
    queryKey: studentKeys.classmates(groupeId || ''),
    queryFn: () => studentService.fetchClassmates(groupeId!),
    enabled: !!groupeId,
    staleTime: 300000, // 5 minutes - classmates don't change often
  });
}

// ==================== Assignments Hooks ====================

export function useAssignments(moduleElementId: string | null, academicYearId?: string, groupeId?: string) {
  return useQuery({
    queryKey: studentKeys.assignments(moduleElementId || '', academicYearId, groupeId),
    queryFn: () => studentService.fetchAssignments(moduleElementId!, academicYearId, groupeId),
    enabled: !!moduleElementId,
    staleTime: 30000,
  });
}

export function useAssignmentDetail(assignmentId: string | null, academicYearId?: string) {
  return useQuery({
    queryKey: studentKeys.assignmentDetail(assignmentId || '', academicYearId),
    queryFn: () => studentService.fetchAssignmentDetail(assignmentId!, academicYearId),
    enabled: !!assignmentId,
    staleTime: 30000,
  });
}

export function useMySubmission(assignmentId: string | null) {
  return useQuery({
    queryKey: studentKeys.submission(assignmentId || ''),
    queryFn: () => studentService.fetchMySubmission(assignmentId!),
    enabled: !!assignmentId,
    staleTime: 30000,
  });
}

export function useSubmitAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assignmentId,
      dto,
    }: {
      assignmentId: string;
      dto: SubmitAssignmentDto;
    }) => studentService.submitAssignment(assignmentId, dto),
    onSuccess: (_, { assignmentId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: studentKeys.assignmentDetail(assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: studentKeys.submission(assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: studentKeys.all,
      });
    },
  });
}

export function useUpdateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      submissionId,
      dto,
    }: {
      submissionId: string;
      assignmentId: string;
      dto: UpdateSubmissionDto;
    }) => studentService.updateSubmission(submissionId, dto),
    onSuccess: (_, { assignmentId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: studentKeys.assignmentDetail(assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: studentKeys.submission(assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: studentKeys.all,
      });
    },
  });
}
